import { gql } from '@apollo/client';

export const CHECKLISTS_PAGE_QUERY = gql`
  query checklists(
    $columnFilters: [ColumnFilter!]
    $limit: Int!
    $offset: Int!
    $orderBy: String
    $orderDirection: String
    $q: String
    $withDeleted: Boolean
  ) {
    checklists(
      columnFilters: $columnFilters
      limit: $limit
      offset: $offset
      orderBy: $orderBy
      orderDirection: $orderDirection
      q: $q
      withDeleted: $withDeleted
    ) {
      id
      currentVersion {
        id
        clientSpecific
        companyId
        createdById
        customPanelId
        defaultResultFamilyId
        deletedAt
        effectiveDate
        name
        serviceSchemeId
        status
        updatedById
        version
        displayCustomPanel
        hasLegalRequirements
        hasRecommendations
      }
      hasDraft
    }
    checklistsPageInfo(
      columnFilters: $columnFilters
      q: $q
      withDeleted: $withDeleted
    ) {
      totalCount
    }
  }
`;

export const CHECKLISTS_QUERY = gql`
  query checklists($limit: Int!, $offset: Int!, $q: String, $withDeleted: Boolean) {
    checklists(limit: $limit, offset: $offset, q: $q, withDeleted: $withDeleted) {
      id
      currentVersion {
        id
        clientSpecific
        companyId
        createdById
        customPanelId
        defaultResultFamilyId
        deletedAt
        effectiveDate
        name
        serviceSchemeId
        status
        updatedById
        version
        displayCustomPanel
        hasLegalRequirements
        hasRecommendations
      }
      hasDraft
    }
  }
`;

export const CHECKLIST_QUERY = gql`
  query checklist($id: Int!) {
    checklist(id: $id) {
      id
      currentVersion {
        id
        checklistSections {
          id
          checklistSectionIdAlias
          isQuickActive
          quickResultId
          name
          parentPosition
          position
          questions {
            id
            alerts {
              negativeAlert
              negativeSeverityId
              resultFamilyOutcome {
                resultId
              }
              resultFamilyOutcomeId
            }
            checklistQuestionId
            checklistQuestionIdAlias
            isHiddenInDraftCap
            conditions {
              action
              checklistQuestionId
              logicalOperator
              position
              resultId
            }
            position
            resultFamilyId
          }
        }
        clientSpecific
        companyId
        createdById
        customPanelId
        defaultResultFamilyId
        deletedAt
        effectiveDate
        name
        serviceSchemeId
        showToFactoryUser
        status
        updatedById
        version
        displayCustomPanel
        hasLegalRequirements
        hasRecommendations
      }
    }
  }
`;

export const CHECKLIST_VERSIONS_QUERY = gql`
  query checklistVersions($limit: Int, $offset: Int, $orderBy: String, $orderDirection: String, $checklistId: Int!) {
    checklistVersions(
      checklistId: $checklistId
      limit: $limit
      offset: $offset
      orderBy: $orderBy
      orderDirection: $orderDirection
    ) {
      id
      checklistId
      checklistSections {
        id
        checklistSectionIdAlias
        isQuickActive
        quickResultId
        name
        parentPosition
        position
        questions {
          id
          alerts {
            negativeAlert
            negativeSeverityId
            resultFamilyOutcome {
              resultId
            }
            resultFamilyOutcomeId
          }
          checklistQuestionId
          checklistQuestionIdAlias
          isHiddenInDraftCap
          conditions {
            action
            checklistQuestionId
            logicalOperator
            position
            resultId
          }
          position
          resultFamilyId
        }
      }
      clientSpecific
      companyId
      createdById
      customPanelId
      defaultResultFamilyId
      deletedAt
      effectiveDate
      name
      serviceSchemeId
      showToFactoryUser
      status
      createdAt
      updatedAt
      updatedById
      version
      displayCustomPanel
      hasLegalRequirements
      hasRecommendations
    }
    checklistVersionsPageInfo(
      checklistId: $checklistId
    ) {
      totalCount
    }
  }
`;

export const CHECKLIST_VERSION_QUERY = gql`
  query checklistVersion($id: Int!) {
    checklistVersion(id: $id) {
      id
      checklistSections {
        id
        checklistSectionIdAlias
        isQuickActive
        quickResultId
        name
        parentPosition
        position
        questions {
          id
          alerts {
            negativeAlert
            negativeSeverityId
            resultFamilyOutcome {
              result {
                id
                currentVersion {
                  code
                  description
                }
              }
              resultId
            }
            resultFamilyOutcomeId
          }
          checklistQuestion {
            id
            currentVersion {
              id
              checklistQuestionIdAlias
              clientSpecificConfigurations {
                id
                companyId
                description
                guidance
                text
                logicType
                serviceIds
              }
              description
              guidance
              negativeResultAlert
              isWildCardQuestion
              negativeResultSeverityId
              nonCompliances {
                id
                description
                laws {
                  id
                  currentVersion {
                    id
                    articleSecNo
                    internalGuidelineInterpretation
                    legalCitation
                    legalReqSummary
                    legislationName
                  }
                }
              }
              text
            }
          }
          checklistQuestionId
          checklistQuestionIdAlias
          isHiddenInDraftCap
          conditions {
            id
            action
            checklistQuestionId
            logicalOperator
            position
            resultId
          }
          position
          resultFamilyId
        }
      }
      clientSpecific
      companyId
      customPanel {
        id
        currentVersion {
          id
          customPanelId
          customPanelSections {
            id
            name
            parentPosition
            position
            questions {
              id
              conditions {
                id
                action
                datasheetQuestionId
                logicalOperator
                position
                predicate
                predicateAddition
                rangeEnd
                rangeStart
                rowName
                value
              }
              datasheetQuestion {
                id
                currentVersion {
                  id
                  autofillSettings {
                    id
                    field
                  }
                  createdById
                  currencySettings {
                    id
                    currencies
                    currencyItems {
                      id
                      code
                      name
                      symbol
                    }
                  }
                  descriptionAlias
                  descriptionExternal
                  dropdownSettings {
                    id
                    allowMultiSelect
                    options {
                      definition
                      value
                    }
                  }
                  dependentDropdownSettings {
                    id
                    dependsOn
                    allowMultiSelect
                    options {
                      definition
                      label
                      parent
                    }
                  }
                  fileSettings {
                    id
                    fileSize
                    fileType
                  }
                  numberSettings {
                    id
                    precision
                  }
                  questionType
                  relationSettings {
                    id
                    allowMultiSelect
                    entity
                    entityItems
                  }
                  status
                  tableSettings {
                    id
                    columns {
                      allowMultiSelect
                      currencies
                      currencyItems {
                        id
                        code
                        name
                        symbol
                      }
                      entity
                      entityItems
                      fileSize
                      fileType
                      name
                      number
                      options {
                        definition
                        value
                      }
                      precision
                      type
                    }
                    enableMultipleRows
                    fixedValues {
                      columnName
                      value
                    }
                    maximizeWidth
                  }
                  text
                  textSettings {
                    id
                    isMaximized
                    isTextarea
                  }
                  translations {
                    id
                    languageId
                    text
                    descriptionAlias
                    options
                  }
                  updatedById
                  version
                }
              }
              datasheetQuestionId
              position
            }
          }
          effectiveDate
          name
          status
        }
      }
      customPanelId
      displayCustomPanel
      hasLegalRequirements
      hasRecommendations
      defaultResultFamily {
        id
        currentVersion {
          code
          name
          resultFamilyOutcomes {
            result {
              id
              currentVersion {
                code
                description
              }
            }
            resultCategory {
              id
              currentVersion {
                code
                name
              }
            }
            resultCategoryId
            resultId
          }
        }
      }
      defaultResultFamilyId
      effectiveDate
      name
      serviceSchemeId
      showToFactoryUser
    }
  }
`;

export const CHECKLIST_VERSION_BASE_QUERY = gql`
  query checklistVersion($id: Int!, $programId: Int, $serviceId: Int) {
    checklistVersion(id: $id, programId: $programId, serviceId: $serviceId) {
      id
      checklistId
      checklistSections {
        id
        checklistSectionIdAlias
        isQuickActive
        quickResultId
        name
        parentPosition
        position
        questions {
          id
          alerts {
            negativeAlert
            negativeSeverityId
            resultFamilyOutcome {
              result {
                id
                currentVersion {
                  code
                  description
                }
              }
              resultId
            }
            resultFamilyOutcomeId
          }
          checklistQuestion {
            id
            currentVersion {
              id
              checklistQuestionIdAlias
              clientSpecificConfigurations {
                id
                companyId
                description
                guidance
                text
                logicType
                serviceIds
              }
              description
              guidance
              negativeResultAlert
              isWildCardQuestion
              negativeResultSeverityId
              nonCompliances {
                id
                description
              }
              text
            }
          }
          checklistQuestionId
          checklistQuestionIdAlias
          isHiddenInDraftCap
          conditions {
            id
            action
            checklistQuestionId
            logicalOperator
            position
            resultId
          }
          position
          resultFamilyId
          resultFamily {
            id
            currentVersion {
              code
              name
              resultFamilyOutcomes {
                result {
                  id
                  currentVersion {
                    code
                    description
                  }
                }
                resultCategory {
                  id
                  currentVersion {
                    code
                    name
                  }
                }
                resultCategoryId
                resultId
              }
            }
          }
        }
      }
      clientSpecific
      companyId
      customPanel {
        id
        currentVersion {
          id
          customPanelId
          customPanelSections {
            id
            name
            parentPosition
            position
            questions {
              id
              conditions {
                id
                action
                datasheetQuestionId
                logicalOperator
                position
                predicate
                predicateAddition
                rangeEnd
                rangeStart
                rowName
                value
              }
              datasheetQuestion {
                id
                currentVersion {
                  id
                  autofillSettings {
                    id
                    field
                  }
                  createdById
                  currencySettings {
                    id
                    currencies
                    currencyItems {
                      id
                      code
                      name
                      symbol
                    }
                  }
                  descriptionAlias
                  descriptionExternal
                  dropdownSettings {
                    id
                    allowMultiSelect
                    options {
                      definition
                      value
                    }
                  }
                  dependentDropdownSettings {
                    id
                    dependsOn
                    allowMultiSelect
                    options {
                      definition
                      label
                      parent
                    }
                  }
                  fileSettings {
                    id
                    fileSize
                    fileType
                  }
                  numberSettings {
                    id
                    precision
                  }
                  questionType
                  relationSettings {
                    id
                    allowMultiSelect
                    entity
                    entityItems
                  }
                  status
                  tableSettings {
                    id
                    columns {
                      allowMultiSelect
                      currencies
                      currencyItems {
                        id
                        code
                        name
                        symbol
                      }
                      entity
                      entityItems
                      fileSize
                      fileType
                      name
                      number
                      options {
                        definition
                        value
                      }
                      precision
                      type
                    }
                    enableMultipleRows
                    fixedValues {
                      columnName
                      value
                    }
                    maximizeWidth
                  }
                  text
                  textSettings {
                    id
                    isMaximized
                    isTextarea
                  }
                  translations {
                    id
                    languageId
                    text
                    descriptionAlias
                    options
                  }
                  updatedById
                  version
                }
              }
              datasheetQuestionId
              position
            }
          }
          effectiveDate
          name
          status
        }
      }
      customPanelId
      displayCustomPanel
      hasLegalRequirements
      hasRecommendations
      defaultResultFamily {
        id
        currentVersion {
          code
          name
          resultFamilyOutcomes {
            result {
              id
              currentVersion {
                code
                description
              }
            }
            resultCategory {
              id
              currentVersion {
                code
                name
              }
            }
            resultCategoryId
            resultId
          }
        }
      }
      defaultResultFamilyId
      effectiveDate
      name
      serviceSchemeId
      showToFactoryUser
    }
  }
`;


export const CHECKLIST_PREVIEW_QUERY = gql`
  query checklistPreview($id: Int!) {
    checklistPreview(id: $id) {
      id
      currentVersion {
        id
        checklistSections {
          id
          checklistSectionIdAlias
          isQuickActive
          quickResultId
          name
          parentPosition
          position
          questions {
            id
            alerts {
              negativeAlert
              negativeSeverityId
              resultFamilyOutcome {
                resultId
              }
              resultFamilyOutcomeId
            }
            checklistQuestion {
              id
              currentVersion {
                id
                checklistQuestionIdAlias
                clientSpecificConfigurations {
                  id
                  companyId
                  description
                  guidance
                  text
                  logicType
                  serviceIds
                }
                description
                guidance
                negativeResultAlert
                isWildCardQuestion
                negativeResultSeverityId
                nonCompliances {
                  id
                  description
                  laws {
                    id
                    currentVersion {
                      id
                      articleSecNo
                      internalGuidelineInterpretation
                      legalCitation
                      legalReqSummary
                      legislationName
                    }
                  }
                }
                text
              }
            }
            checklistQuestionId
            checklistQuestionIdAlias
            isHiddenInDraftCap
            conditions {
              id
              action
              checklistQuestionId
              logicalOperator
              position
              resultId
            }
            position
            resultFamilyId
            resultFamily {
              id
              currentVersion {
                code
                name
                resultFamilyOutcomes {
                  result {
                    id
                    currentVersion {
                      code
                      description
                    }
                  }
                  resultCategory {
                    id
                    currentVersion {
                      code
                      name
                    }
                  }
                  resultCategoryId
                  resultId
                }
              }
            }
          }
        }
        clientSpecific
        companyId
        customPanel {
          id
          currentVersion {
            id
            customPanelId
            customPanelSections {
              id
              name
              parentPosition
              position
              questions {
                id
                conditions {
                  id
                  action
                  datasheetQuestionId
                  logicalOperator
                  position
                  predicate
                  predicateAddition
                  rangeEnd
                  rangeStart
                  rowName
                  value
                }
                datasheetQuestionId
                position
              }
            }
            effectiveDate
            name
            status
          }
        }
        customPanelId
        defaultResultFamily {
          id
          currentVersion {
            code
            name
            resultFamilyOutcomes {
              result {
                id
                currentVersion {
                  code
                  description
                }
              }
              resultCategory {
                id
                currentVersion {
                  code
                  name
                }
              }
              resultCategoryId
              resultId
            }
          }
        }
        defaultResultFamilyId
        effectiveDate
        name
        serviceSchemeId
        showToFactoryUser
        hasLegalRequirements
        hasRecommendations
      }
    }
  }
`;
